import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';
import Illustration from '../../components/ui/Illustration';
import ScopeIllustration, { ILLU_COLOR } from '../../components/ui/ScopeIllustration';
import illuAvancement from '../../assets/illustrations/menu/mon-avancement.svg?raw';
import illuThematiques from '../../assets/illustrations/menu/thematiques.svg?raw';
import illuPerso from '../../assets/illustrations/menu/ma-thematique.svg?raw';
import illuPartage from '../../assets/illustrations/fonctionnalites/codes-quiz.svg?raw';
import illuReponses from '../../assets/illustrations/fonctionnalites/quiz-par-sujet.svg?raw';
import illuMaitrise from '../../assets/illustrations/progression-maitrise.svg?raw';
import illuActivite from '../../assets/illustrations/fonctionnalites/historique.svg?raw';
import illuRevisions from '../../assets/illustrations/revisions-du-jour.svg?raw';
import illuCommencer from '../../assets/illustrations/choisir-un-sujet.svg?raw';
import {
    TrendingUp, TrendingDown, Users, Gift, ChevronRight, Loader2,
} from 'lucide-react';

// ===== Helpers =====

function fmtDate(d) {
    return new Date(d.replace(' ', 'T')).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Tag d'accès : on n'affiche que l'info utile — la date de fin quand il y en a
// une (abonnement, partage, promo, ou l'abonnement lié à une thématique perso).
function accessTag(it) {
    if (it.access === 'free_promo' && it.access_end) return { label: `Gratuit jusqu'au ${fmtDate(it.access_end)}`, cls: 'promo', icon: 'gift' };
    if (it.access === 'shared') return { label: it.access_end ? `Partagé · fin ${fmtDate(it.access_end)}` : 'Accès partagé', cls: 'shared', icon: 'users' };
    if ((it.access === 'subscription' || it.access === 'owner') && it.access_end) return { label: `Jusqu'au ${fmtDate(it.access_end)}`, cls: 'sub', icon: null };
    if (it.access === 'admin') return { label: 'Admin', cls: 'admin', icon: null };
    return null;
}

// Statut synthétique par thématique (la « note » de la carte).
function computeStatus(it) {
    if (it.access === 'expired') return { key: 'expired', label: 'Accès expiré' };
    const days = it.last_activity
        ? (Date.now() - new Date(it.last_activity.replace(' ', 'T'))) / 86400000
        : Infinity;
    const coverage = it.total_questions > 0 ? (it.seen / it.total_questions) * 100 : 0;
    if (days > 30) return { key: 'paused', label: 'En pause' };
    if (it.correct_pct >= 80 && coverage >= 70) return { key: 'strong', label: 'Bien maîtrisé' };
    if (it.correct_pct < 60 || it.sm2_due >= 20) return { key: 'weak', label: 'À consolider' };
    return { key: 'active', label: 'En cours' };
}

function relativeTime(ts) {
    if (!ts) return '—';
    const days = Math.floor((Date.now() - new Date(ts.replace(' ', 'T'))) / 86400000);
    if (days <= 0) return "aujourd'hui";
    if (days === 1) return 'hier';
    if (days < 30) return `il y a ${days} jours`;
    const months = Math.floor(days / 30);
    return months === 1 ? 'il y a 1 mois' : `il y a ${months} mois`;
}

// Étoiles de maîtrise : part des questions de la thématique déjà réussies au
// moins une fois. Jauge de "conquête du pool", complémentaire du % de réussite.
function masteryStars(mastered, total) {
    if (!total) return 0;
    const ratio = mastered / total;
    if (ratio >= 0.8) return 4;
    if (ratio >= 0.55) return 3;
    if (ratio >= 0.3) return 2;
    if (ratio >= 0.1) return 1;
    return 0;
}

function MasteryStars({ mastered, total }) {
    const stars = masteryStars(mastered, total);
    return (
        <span
            className="av-stars"
            title={`${mastered.toLocaleString('fr-FR')} question${mastered > 1 ? 's' : ''} maîtrisée${mastered > 1 ? 's' : ''} sur ${total.toLocaleString('fr-FR')}`}
            aria-label={`Maîtrise : ${stars} étoile${stars > 1 ? 's' : ''} sur 4`}
        >
            {[0, 1, 2, 3].map(i => (
                <span key={i} className={i < stars ? 'av-star av-star--on' : 'av-star'}>★</span>
            ))}
        </span>
    );
}

// Anneau de réussite SVG (l'ancre visuelle de la carte, teintée couleur thématique).
function ScoreRing({ pct, color }) {
    const r = 24;
    const c = 2 * Math.PI * r;
    return (
        <div className="av-ring" role="img" aria-label={`Réussite ${pct} %`}>
            <svg viewBox="0 0 60 60" width="60" height="60">
                <circle cx="30" cy="30" r={r} fill="none" stroke="var(--border)" strokeWidth="5" opacity="0.5" />
                <circle
                    cx="30" cy="30" r={r} fill="none"
                    stroke={color} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * c} ${c}`}
                    transform="rotate(-90 30 30)"
                />
            </svg>
            <span className="av-ring-value">{pct}<small>%</small></span>
        </div>
    );
}

// Partitionnement en 3 zones : Éditoriales / Perso / Partagées.
// Priorité : un accès partagé prime (même si le contenu est de type user).
function groupOf(it) {
    if (it.access === 'shared') return 'shared';
    if (it.content_source === 'user') return 'perso';
    return 'editorial';
}

const SECTIONS = [
    { key: 'editorial', label: 'Thématiques éditoriales', illu: illuThematiques },
    { key: 'perso', label: 'Mes thématiques perso', illu: illuPerso },
    { key: 'shared', label: 'Partagées avec moi', illu: illuPartage },
];

// ===== Carte thématique =====

function ThematiqueCard({ it, navigate }) {
    const color = it.color || 'var(--primary)';
    const status = computeStatus(it);
    const tag = accessTag(it);
    const coverage = it.total_questions > 0
        ? Math.min(100, Math.round((it.seen / it.total_questions) * 100))
        : 0;
    const headStyle = it.image ? {
        backgroundImage: `linear-gradient(135deg, rgba(8,11,19,0.74) 0%, rgba(8,11,19,0.42) 100%), url(${it.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    } : undefined;

    return (
        <article className={`av-card av-card--${status.key}`} style={{ '--av-accent': color }}>
            <div className={`av-card-head ${it.image ? 'av-card-head--img' : ''}`} style={headStyle}>
                <span className="av-card-icon">
                    {it.content_source === 'user'
                        ? <Illustration svg={illuPerso} color={color} className="av-scope-illu" />
                        : <ScopeIllustration icon={it.icon} className="av-scope-illu" size={32} /> }
                </span>
                <div className="av-card-id">
                    <h2 title={it.label}>{it.label}</h2>
                    {tag && (
                        <div className="av-card-tags">
                            <span className={`av-tag av-tag--${tag.cls}`}>
                                {tag.icon === 'gift' && <Gift size={11} />}
                                {tag.icon === 'users' && <Users size={11} />}
                                {tag.label}
                            </span>
                        </div>
                    )}
                </div>
                <span className={`av-status av-status--${status.key}`}>{status.label}</span>
            </div>

            <div className="av-card-body">
                <div className="av-score"><ScoreRing pct={it.correct_pct} color={color} /><span>Réussite</span></div>
                <div className="av-card-metrics">
                    <div className="av-coverage">
                        <div className="av-coverage-label">
                            <span>Questions explorées</span>
                            <span>{it.seen.toLocaleString('fr-FR')} / {it.total_questions.toLocaleString('fr-FR')} questions</span>
                        </div>
                        <div className="av-coverage-track">
                            <div className="av-coverage-fill" style={{ width: `${coverage}%` }} />
                        </div>
                    </div>
                    <div className="av-mastery">
                        <span className="av-mastery-label">Maîtrise</span>
                        <MasteryStars mastered={it.mastered} total={it.total_questions} />

                    </div>
                    <div className="av-metric-row">
                        {it.trend !== null && (
                            <span className={`av-trend ${it.trend >= 0 ? 'av-trend--up' : 'av-trend--down'}`}>
                                {it.trend >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                                {it.trend >= 0 ? '+' : ''}{it.trend} pts / 30 j
                            </span>
                        )}
                        <span className="av-sessions">{it.sessions} session{it.sessions > 1 ? 's' : ''}</span>
                    </div>
                </div>
            </div>

            {it.sm2_due > 0 && (
                <div className="av-review">
                    <Illustration svg={illuRevisions} className="av-review-illu" />
                    <span><strong>{it.sm2_due.toLocaleString('fr-FR')} à revoir aujourd’hui</strong><small>Révisions espacées</small></span>
                </div>
            )}

            <footer className="av-card-foot">
                <span className="av-last">Dernière activité : {relativeTime(it.last_activity)}</span>
                <button className="av-resume" onClick={() => navigate(`/compte/quiz/${it.slug}`)}>
                    {it.access === 'expired' ? 'Renouveler' : 'Reprendre'} <ChevronRight size={15} />
                </button>
            </footer>
        </article>
    );
}

function SummaryStat({ svg, value, label, due = false }) {
    return (
        <div className={`av-stat${due ? ' av-stat--due' : ''}`}>
            <Illustration svg={svg} className="av-stat-illu" />
            <div><strong>{value}</strong><span>{label}</span></div>
        </div>
    );
}

// ===== Page =====

export default function Avancement() {
    usePageTitle('Mon avancement');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get('/user/avancement.php')
            .then(({ data: res }) => setData(res.data))
            .catch(() => setError('Impossible de charger ton avancement.'));
    }, []);

    if (error) {
        return <div className="av-page"><div className="av-error">{error}</div></div>;
    }
    if (!data) {
        return (
            <div className="av-page av-page--loading">
                <Loader2 size={28} className="spin" />
                <p>Calcul de ton avancement…</p>
            </div>
        );
    }

    const { items, global } = data;

    if (!items.length) {
        return (
            <div className="av-page">
                <header className="av-hero">
                    <h1><Illustration svg={illuAvancement} color={ILLU_COLOR} className="av-title-illu" /> Mon avancement</h1>
                </header>
                <div className="av-empty">
                    <Illustration svg={illuCommencer} className="av-empty-illu" />
                    <p>Lance ton premier quiz pour retrouver ici ta progression et les sujets à réviser.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/compte')}>
                        Choisir une thématique <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        );
    }

    const groups = { editorial: [], perso: [], shared: [] };
    items.forEach(it => groups[groupOf(it)].push(it));

    return (
        <div className="av-page">
            {/* ===== Bandeau global ===== */}
            <header className="av-hero">
                <div className="av-hero-title">
                    <h1><Illustration svg={illuAvancement} color={ILLU_COLOR} className="av-title-illu" /> Mon avancement</h1>
                    <p>Tes acquis, tes sujets explorés et tes révisions, au même endroit.</p>
                </div>
                <div className="av-hero-stats">
                    <SummaryStat svg={illuThematiques} value={global.thematiques} label={`thématique${global.thematiques > 1 ? 's' : ''}`} />
                    <SummaryStat svg={illuReponses} value={global.answers.toLocaleString('fr-FR')} label="réponses" />
                    <SummaryStat svg={illuMaitrise} value={`${global.correct_pct} %`} label="réussite globale" />
                    <SummaryStat svg={illuActivite} value={global.active_days} label={`jour${global.active_days > 1 ? 's' : ''} d’activité`} />
                    {global.sm2_due > 0 && <SummaryStat svg={illuRevisions} value={global.sm2_due.toLocaleString('fr-FR')} label="à revoir aujourd’hui" due /> }
                </div>
            </header>

            {/* ===== 3 zones : Éditoriales / Perso / Partagées (masquées si vides) ===== */}
            {SECTIONS.map(({ key, label, illu }) => groups[key].length > 0 && (
                <section key={key} className="av-section">
                    <h2 className="av-section-title">
                        <Illustration svg={illu} color={ILLU_COLOR} className="av-section-illu" />
                        {label}
                        <span className="av-section-count">{groups[key].length}</span>
                    </h2>
                    <div className="av-grid">
                        {groups[key].map(it => (
                            <ThematiqueCard key={it.scope_id} it={it} navigate={navigate} />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
